import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { daysUntil, authedFetch } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  LogOut, Stethoscope, KeyRound, Loader2, Check,
  Gift, Clock, AlertTriangle, CalendarClock, Building2, CreditCard, Users, Send, TrendingUp, PauseCircle,
} from "lucide-react";
import { toast } from "sonner";
import { TopReferrers } from "../components/TopReferrers";
import { DemoVideo } from "../components/DemoVideo";
import { EditableClinicianProfile } from "../components/EditableClinicianProfile";
import { PersistentLayout } from "../components/EditableLayoutRow";
import { AppHeader } from "../components/AppHeader";
import { LaunchStatusBanner } from "../components/LaunchStatusBanner";
import { ClinicianInsights } from "../components/ClinicianInsights";
import { CapacityEditor } from "../components/CapacityEditor";

import { AVAIL_OPTIONS } from "../lib/capacity";

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "—";

// True if the timestamp falls in the current calendar month.
const inCurrentMonth = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
};

const STATUS_LABEL = {
  TRIAL: { label: "Trial", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  ACTIVE: { label: "Active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PAST_DUE: { label: "Past due", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  CANCELLED: { label: "Cancelled", tone: "bg-slate-100 text-slate-600 border-slate-200" },
  PAUSED: { label: "Paused", tone: "bg-violet-50 text-violet-700 border-violet-200" },
  FREE: { label: "Complimentary", tone: "bg-sky-50 text-sky-700 border-sky-200" },
  FREE_LIFETIME: { label: "Free for lifetime", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

export default function ClinicianPortal() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
    const normalizedTab = rawTab === "profile" ? "settings" : rawTab;
    const activeTab = ["overview", "availability", "referrals", "insights", "settings"].includes(normalizedTab) ? normalizedTab : "overview";
  const [clinician, setClinician] = useState(null);
  const [account, setAccount] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notClinician, setNotClinician] = useState(false);
  const [saving, setSaving] = useState(null);
  const [pauseState, setPauseState] = useState(null);
  const [isLive, setIsLive] = useState(true);
  useEffect(() => {
    authedFetch("/site/launch-status?audience=clinician").then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        setIsLive(Boolean(data.is_live));
      }
    }).catch(() => {});
  }, []);
  const [portalLoading, setPortalLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [membershipInvites, setMembershipInvites] = useState([]);
  const [membershipAction, setMembershipAction] = useState(null);

  const openBillingPortal = async () => {
    if (!account) return;
    setPortalLoading(true);
    try {
      const res = await authedFetch("/billing/portal", {
        method: "POST",
        body: JSON.stringify({ account_id: account.id, return_url: `${window.location.origin}/clinician` }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || "Could not open the billing portal.");
        setPortalLoading(false);
      }
    } catch (e) {
      toast.error("Could not open the billing portal.");
      setPortalLoading(false);
    }
  };

  const loadTokens = async (clinId) => {
    const { data } = await supabase
      .from("booking_tokens")
      .select("id,token,gp_clinic_name,created_at")
      .eq("clinician_id", clinId)
      .order("created_at", { ascending: false });
    setTokens(data || []);
    return data || [];
  };

  // Referrals THIS clinician has made to other clinicians (outgoing).
  const loadOutgoing = async (clinId) => {
    const { data: toks } = await supabase
      .from("booking_tokens")
      .select("id,token,created_at,clinician_id")
      .eq("referrer_clinician_id", clinId)
      .order("created_at", { ascending: false });
    const rows = toks || [];
    const ids = [...new Set(rows.map((t) => t.clinician_id).filter(Boolean))];
    const byId = {};
    if (ids.length) {
      const { data: clins } = await supabase
        .from("clinicians")
        .select("id,first_name,last_name,profession")
        .in("id", ids);
      (clins || []).forEach((c) => { byId[c.id] = c; });
    }
    setOutgoing(rows.map((t) => ({ ...t, target: byId[t.clinician_id] || null })));
    return rows;
  };

  useEffect(() => {
    if (!user) return;
    let active = true;
    const load = async () => {
      const fetchClin = async () =>
        (await supabase.from("clinicians").select("*").eq("email", user.email).maybeSingle()).data;
      let clin = await fetchClin();
      if (!active) return;
      const md = user.user_metadata || {};
      if (!clin && md.role === "clinician" && md.ahpra_number) {
        await authedFetch("/onboard/clinician", {
          method: "POST",
          body: JSON.stringify({
            first_name: md.first_name, last_name: md.last_name, profession: md.profession,
            specialty: md.specialty, suburb: md.suburb, state: md.state, postcode: md.postcode,
            phone: md.phone, ahpra_number: md.ahpra_number,
            gender: md.gender, languages: md.languages, photo_url: md.photo_url,
          }),
        });
        clin = await fetchClin();
      }
      if (!active) return;
      if (!clin) {
        setNotClinician(true);
        setLoading(false);
        return;
      }
      setClinician(clin);
      const { data: acct } = await supabase
        .from("accounts").select("*").eq("id", clin.account_id).maybeSingle();
      if (active) setAccount(acct || null);
      if (active && acct) setPauseState(acct.billing_pause_state || "ACTIVE");
      const [, , insightsResponse, inviteResponse] = await Promise.all([loadTokens(clin.id), loadOutgoing(clin.id), authedFetch("/insights/me"), authedFetch("/clinic/membership-invites")]);
      if (active && insightsResponse.ok) setInsights(await insightsResponse.json());
      if (active && inviteResponse.ok) setMembershipInvites((await inviteResponse.json()).incoming || []);
      if (!active) return;
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [user]);

  const respondToMembershipInvite = async (inviteId, action) => {
    setMembershipAction(`${inviteId}:${action}`);
    const response = await authedFetch(`/clinic/membership-invites/${inviteId}/${action}`, { method: "POST" });
    const result = await response.json().catch(() => ({}));
    setMembershipAction(null);
    if (!response.ok) return toast.error(result.detail || "Could not update this clinic invitation.");
    setMembershipInvites((current) => current.filter((invite) => invite.id !== inviteId));
    toast.success(action === "accept" ? "You joined the clinic. Your individual plan has been stopped." : "Clinic invitation declined.");
    if (action === "accept") window.location.reload();
  };

  const handleLogout = async () => {
    await signOut();
    toast.success("Signed out.");
    navigate("/login", { replace: true });
  };

  const updateAvailability = async (value) => {
    if (value === clinician.availability_status) return;
    const isVerified = clinician.verification_status === "VERIFIED" || clinician.is_verified === true;
    if (!isVerified) {
      toast.error("You won’t be able to change your availability until your AHPRA verification is complete.");
      return;
    }
    const isSolo = account?.account_type === "SOLO";
    const wasClosed = clinician.availability_status === "CLOSED_BOOKS";
    if (isSolo && value === "CLOSED_BOOKS") {
      const ok = window.confirm(
        "Close your books?\n\nBecause you're an individual practitioner managing your own calendar, your paid monthly subscription will be paused until you reopen. You'll stay listed on the directory for free as 'not currently accepting referrals'."
      );
      if (!ok) return;
    }
    setSaving(value);
    const res = await authedFetch("/billing/availability", {
      method: "POST",
      body: JSON.stringify({ clinician_id: clinician.id, availability_status: value }),
    });
    setSaving(null);
    if (!res.ok) {
      let msg = "Could not update availability.";
      try { const j = await res.json(); if (j.detail) msg = j.detail; } catch { /* ignore */ }
      toast.error(msg);
      return;
    }
    const data = await res.json();
    setClinician((c) => ({ ...c, availability_status: data.availability_status }));
    if (data.billing_pause_state) setPauseState(data.billing_pause_state);
    if ((isSolo || (data.billing_pause_state && data.billing_pause_state !== "ACTIVE")) && value === "CLOSED_BOOKS") {
      toast.success("Books closed — your monthly subscription will pause until reopened. You stay listed for free as not accepting referrals.");
    } else if (wasClosed && data.billing_pause_state === "ACTIVE") {
      toast.success("Books reopened — your monthly subscription resumes.");
    } else {
      toast.success("Availability updated.");
    }
  };

  if (!authLoading && !user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader firstName={clinician?.first_name || user?.user_metadata?.first_name} role="clinician" />
      <LaunchStatusBanner audience={String(clinician?.profession || user?.user_metadata?.profession || "").toLowerCase().includes("psychiatrist") ? "psychiatrist" : "psychologist"} />

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8 animate-fadein" data-testid="clinician-portal-page">
        {activeTab === "overview" && <div className="mb-4 flex justify-end"><DemoVideo src="/videos/clinician.mp4" title="How clinicians use SimplyRefer" buttonLabel="How it works" variant="outline" size="sm" testId="clinician-demo-video" /></div>}
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500" data-testid="clinician-portal-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading your portal…
          </div>
        ) : notClinician ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="clinician-portal-not-linked">
            <Stethoscope className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-medium text-slate-700">No clinician profile linked to this account</p>
            <p className="mt-1 text-sm text-slate-500">
              We couldn't find a clinician record for <span className="font-medium text-slate-700">{user?.email}</span>.
              If you haven't registered as a practitioner yet, create an account to get listed.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <Button asChild data-testid="clinician-portal-signup-button">
                <Link to="/clinician/signup">Create a clinician account</Link>
              </Button>
              <Button variant="outline" onClick={handleLogout} data-testid="clinician-portal-signout-button">
                Sign out
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {membershipInvites.map((invite) => <div key={invite.id} className="rounded-md border border-violet-200 bg-violet-50 p-5" data-testid={`clinic-membership-invite-${invite.id}`}><div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"><div><p className="font-semibold text-slate-900">Invitation to join {invite.clinic_name}</p><p className="mt-1 text-sm text-slate-600">Accepting keeps your profile, AHPRA verification, availability and referral history together. Your individual subscription will be cancelled first so you are not billed twice.</p></div><div className="flex shrink-0 gap-2"><Button variant="outline" onClick={() => respondToMembershipInvite(invite.id, "decline")} disabled={Boolean(membershipAction)}>Decline</Button><Button onClick={() => respondToMembershipInvite(invite.id, "accept")} disabled={Boolean(membershipAction)}>{membershipAction === `${invite.id}:accept` && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Accept and join</Button></div></div></div>)}
            {/* Individual Practitioner notice */}
            {account?.account_type === "SOLO" && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="individual-practitioner-note">
                You're an <span className="font-semibold text-slate-800">Individual Practitioner</span> — you manage your own profile, availability and bookings here.
              </div>
            )}

            {/* Pending AHPRA verification notice */}
            {!clinician.is_verified && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-5" data-testid="clinician-pending-banner">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">AHPRA verification in progress</p>
                    <p className="mt-0.5 text-sm text-amber-800">
                      Your profile won't appear in the GP directory until SimplyRefer verifies your AHPRA registration. We'll be in touch at <span className="font-medium">{clinician.email}</span>.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Free-trial notice */}
            {account?.subscription_status === "TRIAL" && (
              account.trial_ends_at ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-5" data-testid="clinician-trial-banner">
                  <div className="flex items-start gap-3">
                    <Gift className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                    <div className="flex-1">
                      <p className="font-medium text-emerald-900">You're on your free trial</p>
                      <p className="mt-0.5 text-sm text-emerald-800">
                        {(() => { const d = daysUntil(account.trial_ends_at); return d >= 0
                          ? <>90 days free — <span className="font-semibold inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{d} day{d === 1 ? "" : "s"} left</span>.</>
                          : "Your free trial has ended — choose a plan to stay listed."; })()}
                      </p>
                      <p className="mt-1 text-xs text-emerald-700">{fmtDate(account.trial_started_at)} to {fmtDate(account.trial_ends_at)}</p>
                    </div>
                    <Button asChild size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-100" data-testid="clinician-choose-plan-button">
                      <Link to="/clinic/subscribe">Choose a plan</Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-sky-200 bg-sky-50 p-5" data-testid="clinician-trial-pending-banner">
                  <div className="flex items-start gap-3">
                    <Gift className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
                    <div>
                      <p className="font-medium text-sky-900">Your free trial has not started yet</p>
                      <p className="mt-0.5 text-sm text-sky-800">Your full 90-day free trial begins only when SimplyRefer goes live. A plan and card are optional before launch, and nothing is charged now.</p>
                    </div>
                  </div>
                </div>
              )
            )}

          <PersistentLayout
            scope={String(clinician.profession || "").toLowerCase().includes("psychiatrist") ? "psychiatrist-dashboard-sections" : "psychologist-dashboard-sections"}
            direction="vertical"
            className="flex flex-col gap-8"
            items={[
              { id: "settings", tab: "settings", label: "Settings", node: (<>
          <EditableClinicianProfile clinician={clinician} onUpdated={setClinician} />

              </>) },
              ...(insights?.enabled ? [{ id: "insights", tab: "insights", label: "Insights", node: <ClinicianInsights insights={insights} /> }] : []),
              { id: "referral-activity", tab: "overview", label: "Referral activity", node: (<>
            
            <section className="grid grid-cols-2 gap-4" data-testid="clinician-activity-stats">
              <button
                type="button"
                onClick={() => document.getElementById("received-referrals")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-md border border-slate-200 bg-white p-5 text-left transition-all hover:border-primary/40 hover:shadow-sm"
                data-testid="stat-received-card"
              >
                <div className="flex items-center gap-2 text-slate-500">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-xs font-medium uppercase tracking-wide">Received this month</span>
                </div>
                <p className="mt-1 font-heading text-3xl font-semibold text-slate-900" data-testid="stat-received-month">
                  {insights?.totals?.selected_this_month || 0}
                </p>
                <p className="mt-0.5 text-xs text-slate-400">GP practices that selected you through SimplyRefer</p>
              </button>
              <button
                type="button"
                onClick={() => document.getElementById("sent-referrals")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-md border border-slate-200 bg-white p-5 text-left transition-all hover:border-primary/40 hover:shadow-sm"
                data-testid="stat-sent-card"
              >
                <div className="flex items-center gap-2 text-slate-500">
                  <Send className="h-4 w-4" />
                  <span className="text-xs font-medium uppercase tracking-wide">Sent this month</span>
                </div>
                <p className="mt-1 font-heading text-3xl font-semibold text-slate-900" data-testid="stat-sent-month">
                  {outgoing.filter((t) => inCurrentMonth(t.created_at)).length}
                </p>
                <p className="mt-0.5 text-xs text-slate-400">Referrals you made to other clinicians</p>
              </button>
            </section>

              </>) },
              { id: "subscription", tab: "overview", label: "Subscription", node: (<>
            {/* Subscription & billing */}
            {account && (
              <section data-testid="clinician-billing-section">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-5">
                  <div>
                    <div className="flex items-center gap-2 text-slate-500"><CreditCard className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">Your plan</span></div>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="font-heading text-xl font-semibold text-slate-900" data-testid="clinician-plan-tier">
                        {account.subscription_tier === "SOLO" ? "Individual Practitioner" : account.subscription_tier}
                      </p>
                      {(() => { const s = account.subscription_status === "TRIAL" && !account.trial_ends_at
                        ? { label: "Awaiting trial activation", tone: "bg-sky-50 text-sky-700 border-sky-200" }
                        : (STATUS_LABEL[account.subscription_status] || STATUS_LABEL.TRIAL); return (
                        <span className={`rounded-md border px-2 py-0.5 text-xs font-medium ${s.tone}`} data-testid="clinician-plan-status">{s.label}</span>
                      ); })()}
                    </div>
                    {account.subscription_status === "TRIAL" && (() => { const d = daysUntil(account.trial_ends_at); return d !== null && d >= 0 ? (
                      <p className="mt-1 flex items-center gap-1 text-xs text-emerald-700"><Clock className="h-3.5 w-3.5" />{d} day{d === 1 ? "" : "s"} left on your free trial</p>
                    ) : null; })()}
                    {account.subscription_status === "FREE_LIFETIME" && <p className="mt-1 text-xs font-medium text-indigo-700">Lifetime complimentary access — no subscription payments are due.</p>}
                    {(pauseState === "PAUSED" || pauseState === "PAUSED_PREBILLING") && (
                      <p className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700" data-testid="clinician-billing-paused">
                        <PauseCircle className="h-3.5 w-3.5" /> Paused while books are closed — billing resumes when you reopen.
                      </p>
                    )}
                  </div>
                  {account.subscription_status !== "FREE_LIFETIME" && <div className="flex flex-wrap items-center gap-2">
                    <Button asChild variant="outline" size="sm" data-testid="clinician-change-plan-button">
                      <Link to="/clinic/subscribe">{account.subscription_status === "TRIAL" ? "Choose a plan" : "Change plan"}</Link>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={openBillingPortal} disabled={portalLoading} data-testid="clinician-manage-billing-button">
                      {portalLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                      Manage billing
                    </Button>
                  </div>}
                </div>
              </section>
            )}

              </>) },
              { id: "availability", tab: "availability", label: "Availability", node: (<>
            {/* Availability settings */}
            <section data-testid="clinician-availability-section">
              <h2 className="font-heading text-lg font-semibold text-slate-900">Availability</h2>
              <p className="mt-1 text-sm text-slate-500">Let referring GPs know if you're accepting patients.</p>
              {!(clinician.verification_status === "VERIFIED" || clinician.is_verified === true) && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="availability-locked-notice">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-sm text-amber-800">You won’t be able to change your availability until your AHPRA verification is complete.</p>
                </div>
              )}
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {AVAIL_OPTIONS.map((opt) => {
                  const active = clinician.availability_status === opt.value;
                  const isSaving = saving === opt.value;
                  const locked = !(clinician.verification_status === "VERIFIED" || clinician.is_verified === true);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => updateAvailability(opt.value)}
                      disabled={!!saving || locked}
                      data-testid={`availability-set-${opt.value}`}
                      style={active ? opt.style : undefined}
                      className={`relative flex flex-col rounded-md border p-4 text-left transition-all duration-200 ${locked ? "cursor-not-allowed opacity-60 " : ""}${
                        active ? "ring-2 ring-primary/30" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">{opt.label}</span>
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                        ) : active ? (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground" data-testid={`availability-active-${opt.value}`}>
                            <Check className="h-3 w-3" />
                          </span>
                        ) : null}
                      </div>
                      <span className="mt-1 text-xs text-slate-500">{opt.desc}</span>
                    </button>
                  );
                })}
                {(() => {
                  const locked291 = !(clinician.verification_status === "VERIFIED" || clinician.is_verified === true);
                  const on291 = clinician.assessment_291_status && clinician.assessment_291_status !== "NOT_OFFERED";
                  const preLaunch291 = !clinician.is_sample && !isLive;
                  const toggle291 = async () => {
                    if (locked291) return;
                    const nextStatus = on291 ? "NOT_OFFERED" : "AVAILABLE_NOW";
                    const prev = { status: clinician.assessment_291_status, from: clinician.assessment_291_available_from };
                    setClinician((c) => ({ ...c, assessment_291_status: nextStatus, assessment_291_available_from: nextStatus === "NOT_OFFERED" ? null : c.assessment_291_available_from }));
                    const res = await authedFetch("/billing/assessment-291", {
                      method: "POST",
                      body: JSON.stringify({ clinician_id: clinician.id, assessment_291_status: nextStatus, assessment_291_available_from: nextStatus === "AVAILABLE_FROM" ? clinician.assessment_291_available_from : null }),
                    });
                    if (!res.ok) {
                      setClinician((c) => ({ ...c, assessment_291_status: prev.status, assessment_291_available_from: prev.from }));
                      let msg = "Could not update one-off assessment availability.";
                      try { const j = await res.json(); if (j.detail) msg = j.detail; } catch {}
                      toast.error(msg);
                      return;
                    }
                    toast.success(on291 ? "291 assessments turned off." : "291 assessments turned on \u2013 works alongside your status above.");
                  };
                  return (
                    <button
                      key="assessment_291"
                      onClick={toggle291}
                      disabled={!!saving || locked291}
                      data-testid="availability-toggle-291"
                      style={on291 ? { backgroundColor: "#FFFBEB", borderColor: "#FCD34D" } : undefined}
                      className={`relative flex flex-col rounded-md border p-4 text-left transition-all duration-200 ${locked291 ? "cursor-not-allowed opacity-60" : on291 ? "" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">291 assessments <span className="font-normal text-slate-400">(optional)</span></span>
                        {on291 ? (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: "#92400E" }} data-testid="availability-active-291">
                            <Check className="h-3 w-3 text-white" />
                          </span>
                        ) : null}
                      </div>
                      <span className="mt-1 text-xs text-slate-500">Works alongside whichever status you pick above</span>
                    </button>
                  );
                })()}
              </div>
              {clinician.assessment_291_status && clinician.assessment_291_status !== "NOT_OFFERED" && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4 shadow-sm md:ml-[calc(66.666667%+0.5rem)] md:w-[calc(33.333333%-0.5rem)]" data-testid="assessment-291-date-field">
                  <div className="flex items-center justify-between gap-3"><label className="text-sm font-semibold text-slate-900">When can you start offering 291 assessments?</label><span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800">Optional</span></div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">Leave blank if you are available now. This date only applies to 291 assessments.</p>
                  <input
                    type="date"
                    className="mt-3 w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 shadow-sm outline-none transition hover:border-amber-300 focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
                    data-testid="assessment-291-date"
                    value={clinician.assessment_291_available_from || ""}
                    onChange={async (e) => {
                      const dateVal = e.target.value;
                      const prev = { status: clinician.assessment_291_status, from: clinician.assessment_291_available_from };
                        const nextStatus = dateVal ? "AVAILABLE_FROM" : "AVAILABLE_NOW";
                      setClinician((c) => ({ ...c, assessment_291_status: nextStatus, assessment_291_available_from: dateVal }));
                      const res = await authedFetch("/billing/assessment-291", {
                        method: "POST",
                        body: JSON.stringify({ clinician_id: clinician.id, assessment_291_status: nextStatus, assessment_291_available_from: dateVal || null }),
                      });
                      if (!res.ok) {
                        setClinician((c) => ({ ...c, assessment_291_status: prev.status, assessment_291_available_from: prev.from }));
                        let msg = "Could not save that date.";
                        try { const j = await res.json(); if (j.detail) msg = j.detail; } catch {}
                        toast.error(msg);
                      }
                    }}
                  />
                </div>
              )}
              <CapacityEditor
                clinician={clinician}
                onUpdated={setClinician}
                locked={!(clinician.verification_status === "VERIFIED" || clinician.is_verified === true) || (!clinician.is_sample && !isLive)}
              />
            </section>

              </>) },
              { id: "top-referrers", tab: "insights", label: "Top referrers", node: (<>
            {/* Top referring GP clinics */}
            <TopReferrers rows={insights?.totals?.top_referrers || []} testId="clinician-top-referrers" />

              </>) },
              { id: "received-referrals", tab: "referrals", label: "Received referrals", node: (<>
            {/* Optional SimplyRefer References received */}
            <section id="received-referrals" data-testid="clinician-referrals-section">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold text-slate-900">SimplyRefer References received</h2>
                <span className="text-sm text-slate-500" data-testid="clinician-referrals-count">
                  {tokens.length} total
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-500">When a patient quotes a reference, match it here to see which GP clinic referred them.</p>
              {tokens.length === 0 ? (
                <div className="mt-4 rounded-md border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="clinician-referrals-empty">
                  <KeyRound className="mx-auto h-7 w-7 text-slate-300" />
                  <p className="mt-2 text-sm text-slate-500">No SimplyRefer References yet.</p>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {tokens.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-4" data-testid={`clinician-token-${t.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 font-mono text-xs font-semibold text-blue-700">
                          <KeyRound className="h-3 w-3" /> #{t.token}
                        </span>
                        <span className="flex items-center gap-1.5 text-sm text-slate-700" data-testid={`clinician-token-clinic-${t.id}`}>
                          <Building2 className="h-4 w-4 text-slate-400" /> {t.gp_clinic_name}
                        </span>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <CalendarClock className="h-3.5 w-3.5" /> {fmtDateTime(t.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

              </>) },
              { id: "sent-referrals", tab: "referrals", label: "Sent referrals", node: (<>
            {/* Referrals I've made (outgoing) */}
            <section id="sent-referrals" data-testid="clinician-outgoing-section">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold text-slate-900">Referrals I've made</h2>
                <span className="text-sm text-slate-500" data-testid="clinician-outgoing-count">
                  {outgoing.length} total
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-500">Optional SimplyRefer References you created when referring to another clinician.</p>
              {outgoing.length === 0 ? (
                <div className="mt-4 rounded-md border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="clinician-outgoing-empty">
                  <Send className="mx-auto h-7 w-7 text-slate-300" />
                  <p className="mt-2 text-sm text-slate-500">You haven't referred anyone yet.</p>
                  <Button asChild variant="outline" size="sm" className="mt-4" data-testid="clinician-outgoing-refer-button">
                    <Link to="/clinicians"><Users className="mr-2 h-4 w-4" /> Find a verified clinician</Link>
                  </Button>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {outgoing.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-4" data-testid={`clinician-outgoing-${t.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 font-mono text-xs font-semibold text-blue-700">
                          <KeyRound className="h-3 w-3" /> #{t.token}
                        </span>
                        <span className="flex items-center gap-1.5 text-sm text-slate-700" data-testid={`clinician-outgoing-target-${t.id}`}>
                          <Stethoscope className="h-4 w-4 text-slate-400" />
                          {t.target
                            ? `${t.target.first_name || ""} ${t.target.last_name || ""}`.trim() + (t.target.profession ? ` — ${t.target.profession}` : "")
                            : "Clinician"}
                        </span>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <CalendarClock className="h-3.5 w-3.5" /> {fmtDateTime(t.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
              </>) },
            ].filter((item) => item.tab === activeTab)}
          />
          </div>
        )}
      </main>
    </div>
  );
}
